// Reparto de la devolución de una anulación — mismas reglas que
// `repartoDeDevolucion` de pos-graciela-api (src/domain/anulacionVenta.js;
// SPEC_ANULACION_FACTURAS_Y_NOTAS_CREDITO_v0.2.md RN-AN19 y RN-AN51, refinadas
// 2026-10-11). El API es quien decide; esto sólo evita mandar un pedido que va
// a rechazar y le dice al operador por qué.

const redondear = (n) => Math.round(Number(n || 0) * 100) / 100;

// Agrupa por método y descarta montos no positivos, como `egresoPrevisto`.
export const agrupar = (formas = []) => {
  const porMetodo = new Map();
  for (const f of formas || []) {
    const metodo = String(f?.metodo_pago || '').trim().toUpperCase();
    const monto = redondear(f?.monto);
    if (!metodo || monto <= 0) continue;
    porMetodo.set(metodo, redondear((porMetodo.get(metodo) || 0) + monto));
  }
  return [...porMetodo.entries()]
    .map(([metodo_pago, monto]) => ({ metodo_pago, monto }))
    .sort((a, b) => a.metodo_pago.localeCompare(b.metodo_pago));
};

export const total = (formas = []) => redondear(formas.reduce((acc, f) => acc + Number(f.monto || 0), 0));

export const efectivo = (formas = []) => redondear(
  agrupar(formas).filter((f) => f.metodo_pago === 'EFECTIVO').reduce((acc, f) => acc + f.monto, 0)
);

/**
 * evaluarReparto — ¿se puede mandar este reparto?
 *
 * @param cobrado            [{ metodo_pago, monto }] lo que cobró la venta
 * @param elegido            [{ metodo_pago, monto }] lo que el operador eligió
 * @param efectivoDisponible efectivo esperado de la caja (null = sin caja)
 * @returns { valido, cambiado, error, efectivoRequerido }
 */
export const evaluarReparto = ({ cobrado = [], elegido = [], efectivoDisponible = null } = {}) => {
  const original = agrupar(cobrado);
  for (const f of elegido) {
    if (!(redondear(f?.monto) > 0)) {
      return { valido: false, cambiado: false, error: 'Cada forma tiene que tener un monto mayor a cero.', efectivoRequerido: 0 };
    }
  }
  const formas = agrupar(elegido);
  if (!formas.length) {
    return { valido: false, cambiado: false, error: 'Indicá al menos una forma de devolución.', efectivoRequerido: 0 };
  }
  if (total(formas) !== total(original)) {
    return {
      valido: false,
      cambiado: false,
      error: `El total devuelto tiene que ser ${total(original).toLocaleString('es-PY')}, igual a lo cobrado.`,
      efectivoRequerido: efectivo(formas)
    };
  }
  const clave = (lista) => lista.map((f) => `${f.metodo_pago}:${f.monto}`).join('|');
  const efectivoRequerido = efectivo(formas);
  // Sin efectivo a devolver no se controla, aunque el cajón esté en negativo.
  if (efectivoDisponible != null && efectivoRequerido > 0 && efectivoRequerido > Number(efectivoDisponible)) {
    return {
      valido: false,
      cambiado: clave(formas) !== clave(original),
      error: `En la caja hay ${Math.round(Number(efectivoDisponible)).toLocaleString('es-PY')} en efectivo y hay que devolver ${efectivoRequerido.toLocaleString('es-PY')}. Elegí otra caja o devolvé por otro medio.`,
      efectivoRequerido
    };
  }
  return { valido: true, cambiado: clave(formas) !== clave(original), error: null, efectivoRequerido };
};
